import React, { Suspense, useEffect, useState } from 'react';
import styled, { useTheme } from 'styled-components';
import AsideComponent from '../components/Aside/Aside.component';
import HeaderComponent from '../components/Header/Header.component';
import { Navigate, Route, Routes } from 'react-router-dom';
import NotFoundPage from './pages/NotFound.page';
import { RoutesConstant } from '../constants/routes.constant';
import CandidatesPage from './pages/Candidates/Candidates.page';
import { useAppDispatch } from '../hooks/useAppDispatcher.hook';
import { useAppSelector } from '../hooks/useAppSelector.hook';
import { getGlobalLoading } from '../redux/App/App.slice';
import { Transition } from 'react-transition-group';
import PortalComponent from '../components/Portal.component';
import SpinnerComponent from '../components/Spinner.component';
import CandidatePage from './pages/Candidate/Candidate.page';
import ReportPage from './pages/Report/Report.page';
import { useModalManager } from '../hooks/ModalManager.hook';
import NoAccessModalComponent from '../components/Modal/NoAccessModal.component';
import InDevelopingPage from './pages/InDeveloping.page';
import { ConnectToastToContainer, useToast } from '../hooks/useToast';
import { getCentrifugoConnectThunk } from '../redux/Centrifugo/CentrifugoThunk';
import { useTranslation } from 'react-i18next';
import { resetCentrifugoChannels } from '../redux/Centrifugo/CentrifugoSlice';
import { ConnectNotificationToContainer } from '../hooks/useNotification.hook';
import { getCandidateMetaThunk } from '../redux/Candidate/Candidate.thunk';
import ModalBuilderComponent, {
  ModalBuilderTypes,
} from '../components/Modal/ModalBuilder.component';
import ExtensionPage from './pages/Extension.page';
import AssistantLifecycle from './pages/Assistant/AssistantLifecycle.component';
import {
  CentrifugoEventType,
  ConnectCentrifugoToContainer,
  useCentrifugoManager,
} from '../hooks/CentrifugoManager.hook';
import InitScreen from './InitScreen';
import DashboardPage from './pages/Dashboard/Dashboard.page';
import ControlPanelPage from './pages/ControlPanel/ControlPanel.page';
import { UserRole } from '../types/userRole.types';
import EventsPage from './pages/Events/Events.page';
import {
  checkUserRequiredPermissions,
  checkUserRequiredRole,
} from '../utils/getUserRolesByToken';
import useAuthHook from '../hooks/useAuth.hook';
import { UserPermissions } from '../types/userPermissions.types';
import InternshipPage from './pages/Internship/Internship.page';
import InternshipsPage from './pages/Internships/Internships.page';
import ProfilePage from './pages/Profile.page';
import InvitationCandidatePage from './pages/InvitationCandidate/InvitationCandidate.page';
import Snowfall from 'react-snowfall';
import dayjs from 'dayjs';
import { getSnowFall } from '../redux/SnowFall/SnowFall.slice';

const AssistantPage = React.lazy(
  () => import('./pages/Assistant/Assistant.page'),
);

const StyledWrapperContainer = styled.div`
  display: grid;
  flex: 1;
  grid-template-rows: 60px auto;
  overflow-x: auto;
`;

const StyledRootContainer = styled.div`
  min-height: 100vh;
  display: flex;
`;

export interface RootContainerProps {
  hasAccessError?: boolean;
}

const RootContainer = ({ hasAccessError }: RootContainerProps) => {
  const theme = useTheme() as any;
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { openModal } = useModalManager();
  const { subscribe, unsubscribe } = useCentrifugoManager();
  const isGlobalLoading = useAppSelector(getGlobalLoading);
  const { promiseToast } = useToast();
  const { user } = useAuthHook();
  const [isLoading, setLoading] = useState(true);
  const [hasAccess, setHasAccess] = useState(true);
  const [isReadyNotification, setReadyNotification] = useState(false);
  const userRoleIds = user.regions?.map((item: any) => item?.id);

  const isHeadUser = checkUserRequiredRole(user.roles, [
    UserRole.HEAD,
    UserRole.DEV_HEAD,
  ]);

  const checkUserRole = (
    WrappedComponent: React.ComponentType,
    requiredRole: UserRole[],
  ) => {
    const hasRequiredRole = checkUserRequiredRole(user.roles, requiredRole);

    return hasRequiredRole ? <WrappedComponent /> : <Navigate to={'/'} />;
  };

  const checkUserPermission = (
    WrappedComponent: React.ComponentType,
    requiredPermissions: UserPermissions[],
  ) => {
    const hasRequiredPermission = checkUserRequiredPermissions(
      user.permissions,
      requiredPermissions,
    );
    return hasRequiredPermission ? <WrappedComponent /> : <Navigate to={'/'} />;
  };
  useEffect(() => {
    if (!hasAccess) {
      setLoading(false);
    }
  }, [hasAccess]);

  useEffect(() => {
    if (hasAccessError) {
      setHasAccess(false);
    } else {
      Promise.all([dispatch(getCandidateMetaThunk(userRoleIds))])
        .then(() => {
          promiseToast(dispatch(getCentrifugoConnectThunk()), {
            loading: t('notifications.toast.loading'),
            success: t('notifications.toast.success'),
            error: t('notifications.toast.error'),
            catch: (err: any) => {
              console.error(err);
            },
          });
          setHasAccess(true);
          setLoading(false);
          setReadyNotification(true);
        })
        .catch(_ => {
          setHasAccess(false);
        });

      subscribe(CentrifugoEventType.TRIGGER_EVENT, ({ notification }: any) => {
        // @ts-ignore
        openModal(ModalBuilderComponent, notification as ModalBuilderTypes);
      });

      return () => {
        unsubscribe(CentrifugoEventType.TRIGGER_EVENT);
        dispatch(resetCentrifugoChannels(undefined));
      };
    }
  }, [dispatch, hasAccessError]);

  const isWinter = [11, 0, 1].includes(dayjs().month());
  const snowfallActive = useAppSelector(getSnowFall);

  return !isLoading ? (
    hasAccess ? (
      <React.Fragment>
        <StyledRootContainer>
          <ConnectToastToContainer />
          <ConnectCentrifugoToContainer />
          <AssistantLifecycle />
          <AsideComponent />
          <StyledWrapperContainer>
            <HeaderComponent />
            <Suspense fallback={<SpinnerComponent />}>
              <Routes>
              <Route
                path={RoutesConstant.DASHBOARD}
                element={checkUserRole(DashboardPage, [
                  UserRole.HR,
                  UserRole.DEV_HR,
                  UserRole.SUPER_HR,
                  UserRole.DEV_SUPER_HR,
                  UserRole.ADMIN,
                  UserRole.DEV_ADMIN,
                ])}
              />
              <Route
                path={RoutesConstant.CANDIDATE}
                element={checkUserPermission(CandidatePage, [
                  UserPermissions.CAN_VIEW_CANDIDATE,
                ])}
              />
              <Route
                path={RoutesConstant.CANDIDATES_GROUP}
                element={<CandidatesPage />}
              />
              <Route
                path={RoutesConstant.INTERNSHIPS}
                element={<InternshipsPage />}
              />
              <Route
                path={RoutesConstant.EVENTS}
                element={checkUserRole(EventsPage, [
                  UserRole.HR,
                  UserRole.DEV_HR,
                  UserRole.SUPER_HR,
                  UserRole.DEV_SUPER_HR,
                  UserRole.ADMIN,
                  UserRole.DEV_ADMIN,
                ])}
              />
              <Route
                path={RoutesConstant.REPORT_GROUP}
                element={checkUserRole(ReportPage, [
                  UserRole.HR,
                  UserRole.DEV_HR,
                  UserRole.SUPER_HR,
                  UserRole.DEV_SUPER_HR,
                  UserRole.ADMIN,
                  UserRole.DEV_ADMIN,
                ])}
              />
              <Route
                path={RoutesConstant.REPORT}
                element={<Navigate to={RoutesConstant.REPORT_GROUPS.JOB} />}
              />
              <Route
                path={RoutesConstant.CONTROL_PANEL}
                element={checkUserRole(ControlPanelPage, [
                  UserRole.SUPER_HR,
                  UserRole.DEV_SUPER_HR,
                  UserRole.ADMIN,
                  UserRole.DEV_ADMIN,
                ])}
              />
              <Route
                path={RoutesConstant.INTERNSHIP}
                element={<InternshipPage />}
              />
              {/*<Route*/}
              {/*  path={RoutesConstant.INTERNSHIPS_GROUP}*/}
              {/*  element={<InternshipsPage />}*/}
              {/*/>*/}
              <Route
                path={RoutesConstant.CANDIDATE_R}
                element={
                  <Navigate to={RoutesConstant.CANDIDATES_GROUPS.WAIT} />
                }
              />
              <Route
                path={RoutesConstant.CANDIDATES}
                element={
                  <Navigate
                    to={
                      isHeadUser
                        ? RoutesConstant.CANDIDATES_GROUPS.ALL
                        : RoutesConstant.CANDIDATES_GROUPS.WAIT
                    }
                  />
                }
              />
              <Route
                path={RoutesConstant.ASSISTANT}
                element={checkUserPermission(AssistantPage, [
                  UserPermissions.CAN_VIEW_CANDIDATE,
                ])}
              />
              <Route
                path={RoutesConstant.HELP}
                element={<InDevelopingPage />}
              />
              <Route path={RoutesConstant.PROFILE} element={<ProfilePage />} />
              <Route
                path={RoutesConstant.INVITATION}
                element={checkUserPermission(InvitationCandidatePage, [
                  UserPermissions.CAN_VIEW_CANDIDATE,
                ])}
              />
              {/*<Route*/}
              {/*  path={RoutesConstant.INTERNSHIPS}*/}
              {/*  element={*/}
              {/*    <Navigate to={RoutesConstant.INTERNSHIPS_GROUPS.WORK} />*/}
              {/*  }*/}
              {/*/>*/}
              <Route
                path={RoutesConstant.EXTENSION}
                element={<ExtensionPage />}
              />
              <Route
                path="/"
                element={
                  <Navigate to={
                    isHeadUser
                      ? RoutesConstant.CANDIDATES_GROUPS.ALL
                      : RoutesConstant.CANDIDATES_GROUPS.WAIT
                  } />
                }
              />
              <Route path="*" element={<NotFoundPage />} />
              </Routes>
            </Suspense>
          </StyledWrapperContainer>
          <Transition
            in={isGlobalLoading}
            unmountOnExit={true}
            timeout={{
              exit: 300,
            }}
          >
            {state => (
              <PortalComponent
                style={{
                  ...theme.animations.defaultState,
                  ...theme.animations.defaultSteps[state],
                }}
              >
                <SpinnerComponent style={{ color: 'white' }} />
              </PortalComponent>
            )}
          </Transition>
          {isReadyNotification && <ConnectNotificationToContainer />}
          {isWinter && snowfallActive && (
            <Snowfall
              color="white"
              snowflakeCount={100}
            />
          )}
        </StyledRootContainer>
      </React.Fragment>
    ) : (
      <NoAccessModalComponent />
    )
  ) : (
    <InitScreen stepLabel="Preparation for work..." />
  );
};

export default RootContainer;
